import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaHeart, FaMapMarkerAlt, FaStar, FaTrash, FaArrowLeft, FaSearch, FaFilter } from 'react-icons/fa';
import Navbar from '../components/Navbar';
import { getPropertyImage } from '../utils/imageHelper';
import { fetchWishlist, removeFromWishlist, invalidateWishlistCache } from '../utils/wishlistAPI';
import useSmartMatch from '../hooks/useSmartMatch';
import { SmartMatchBadge } from '../components/SmartMatchBadge';
import './WishlistPage.css';
import '../components/SmartMatchBadge.css';

const PROPERTY_TYPES = ['All', 'Hotel', 'Villa', 'Apartment', 'Cottage', 'House', 'Resort', 'Heritage', 'Camp'];

const WishlistPage = () => {
  const navigate = useNavigate();
  const [allFavorites, setAllFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [removingId, setRemovingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('All');

  const loadFavorites = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      invalidateWishlistCache();
      const items = await fetchWishlist();
      setAllFavorites(Array.isArray(items) ? items : []);
    } catch (err) {
      console.error('Error loading wishlist:', err);
      setError('Failed to load wishlist. Please try again.');
      setAllFavorites([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFavorites();
  }, [loadFavorites]);

  const handleRemove = async (propertyId) => {
    setRemovingId(String(propertyId));
    // Optimistic
    setAllFavorites((prev) => prev.filter((p) => String(p._id || p.propertyId) !== String(propertyId)));
    try {
      await removeFromWishlist(propertyId);
      window.dispatchEvent(new Event('storage'));
    } catch (err) {
      console.error('Remove failed:', err);
      loadFavorites(); // re-sync on error
    } finally {
      setRemovingId(null);
    }
  };

  const handleView = (property) => {
    const id = property._id || property.id || property.propertyId;
    navigate(`/property/${id}`);
  };

  const { scoredProperties } = useSmartMatch(allFavorites);

  const matchById = React.useMemo(() => {
    const map = new Map();
    scoredProperties.forEach((p) => {
      map.set(String(p._id || p.propertyId || p.id), p.smartMatch);
    });
    return map;
  }, [scoredProperties]);

  // Derived: filtered list
  const filtered = allFavorites.filter((p) => {
    const matchesSearch =
      !searchTerm ||
      (p.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.city || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType =
      typeFilter === 'All' ||
      (p.type || '').toLowerCase() === typeFilter.toLowerCase();
    return matchesSearch && matchesType;
  });

  if (loading) {
    return (
      <div className="wishlist-page">
        <Navbar />
        <div className="wishlist-loading">
          <div className="spinner" />
          <p>Loading your wishlist…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="wishlist-page">
      <Navbar />
      <div className="wishlist-container">

        {/* Header */}
        <div className="wishlist-header">
          <button className="back-button" onClick={() => navigate(-1)}>
            <FaArrowLeft /><span>Back</span>
          </button>
          <div className="wishlist-title-row">
            <h1>
              <FaHeart className="wl-title-heart" /> Your Wishlist
            </h1>
            <span className="wl-count-badge">
              {allFavorites.length} {allFavorites.length === 1 ? 'property' : 'properties'}
            </span>
          </div>
          <p className="wishlist-subtitle">
            {allFavorites.length === 0
              ? "You haven't saved any properties yet."
              : 'Saved across all your devices. Remove anytime.'}
          </p>
        </div>

        {error && (
          <div className="wl-error">
            <p>{error}</p>
            <button className="wl-retry-btn" onClick={loadFavorites}>Retry</button>
          </div>
        )}

        {/* Filters (only show when there are items) */}
        {allFavorites.length > 0 && (
          <div className="wl-filters">
            <div className="wl-search-wrap">
              <FaSearch className="wl-search-icon" />
              <input
                type="text"
                placeholder="Search by name or city…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="wl-search-input"
              />
            </div>
            <div className="wl-type-filter">
              <FaFilter className="wl-filter-icon" />
              <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="wl-type-select">
                {PROPERTY_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Empty state */}
        {allFavorites.length === 0 && !error && (
          <div className="empty-wishlist">
            <FaHeart className="empty-icon" />
            <h2>No saved properties yet</h2>
            <p>Tap the heart icon on any property to save it here.</p>
            <button className="btn-explore" onClick={() => navigate('/search')}>
              Explore Properties
            </button>
          </div>
        )}

        {/* No results after filter */}
        {allFavorites.length > 0 && filtered.length === 0 && (
          <div className="empty-wishlist">
            <FaSearch className="empty-icon" style={{ color: '#94a3b8' }} />
            <h2>No results</h2>
            <p>Try a different search or filter.</p>
          </div>
        )}

        {/* Grid */}
        {filtered.length > 0 && (
          <div className="favorites-grid">
            {filtered.map((property) => {
              const propertyId = property._id || property.propertyId || property.id;
              const imageUrl = getPropertyImage(
                property,
                'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&q=80'
              );
              const isRemoving = removingId === String(propertyId);

              return (
                <div
                  key={propertyId}
                  className={`favorite-card${isRemoving ? ' removing' : ''}`}
                >
                  <div className="favorite-image" onClick={() => handleView(property)}>
                    <img
                      src={imageUrl}
                      alt={property.name}
                      onError={(e) => {
                        e.target.src =
                          'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&q=80';
                      }}
                    />
                    {property.type && (
                      <span className="wl-type-badge">{property.type}</span>
                    )}
                    <button
                      className="remove-favorite-btn"
                      onClick={(e) => { e.stopPropagation(); handleRemove(propertyId); }}
                      aria-label="Remove from wishlist"
                      disabled={isRemoving}
                    >
                      <FaTrash />
                    </button>
                    <button
                      className="favorite-heart-btn active"
                      onClick={(e) => { e.stopPropagation(); handleRemove(propertyId); }}
                      aria-label="Remove from wishlist"
                      disabled={isRemoving}
                    >
                      <FaHeart />
                    </button>
                  </div>

                  <div className="favorite-content" onClick={() => handleView(property)}>
                    <div className="favorite-name-row">
                      <h3 className="favorite-name">{property.name || '—'}</h3>
                      {matchById.get(String(propertyId)) && (
                        <SmartMatchBadge match={matchById.get(String(propertyId))} compact showReasons={false} />
                      )}
                    </div>
                    <div className="favorite-location">
                      <FaMapMarkerAlt />
                      <span>{property.city || property.address || 'Location not specified'}</span>
                    </div>
                    {property.rating > 0 && (
                      <div className="favorite-rating">
                        <FaStar className="star-icon" />
                        <span>{Number(property.rating).toFixed(1)}</span>
                        {property.reviews > 0 && (
                          <span className="reviews-count">({property.reviews} reviews)</span>
                        )}
                      </div>
                    )}
                    {property.pricePerNight > 0 && (
                      <div className="favorite-price">
                        <span className="price-amount">
                          ₹{Number(property.pricePerNight).toLocaleString()}
                        </span>
                        <span className="price-label">/ night</span>
                      </div>
                    )}
                    <button
                      className="btn-view-details"
                      onClick={(e) => { e.stopPropagation(); handleView(property); }}
                    >
                      View Details
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default WishlistPage;
